import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useArea } from '../../contexts/AreaContext';
import { ServiceType } from '../../types';
import { ADMIN_AREA, AppArea, AREA_THEME, getMenuItems } from '../../config/areaConfig';
import { Icon, IconByName } from '../ui';
import PasswordChangeForm from '../Forms/PasswordChangeForm';
import './Sidebar.css';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const AREA_ORDER: AppArea[] = [ServiceType.HOME_CARE, ServiceType.LIVE_IN_CARE, ADMIN_AREA];

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const { area, setArea, resetAreaSelection } = useArea();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showAreaMenu, setShowAreaMenu] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const theme = AREA_THEME[area];
  const menuItems = getMenuItems(area, isAdmin());

  const handleSwitchArea = (a: AppArea) => {
    setShowAreaMenu(false);
    if (a === area) return;
    setArea(a);
    // Auf einer Seite landen, die es im Zielbereich gibt — die Verwaltung hat
    // kein Dashboard, die Geschäftsfelder keinen Posteingang.
    navigate(AREA_THEME[a].home);
    onClose();
  };

  const handleLogout = async () => {
    try {
      await logout();
      resetAreaSelection();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && <div className="sidebar-overlay" onClick={onClose} />}

      <aside className={`sidebar ${isOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          {/* Das Logo führt — wie auf der Website — zurück auf die Startseite. */}
          <Link
            to="/"
            className="sidebar-logo"
            title="Zur Startseite"
            aria-label="Zur Startseite"
            onClick={onClose}
          >
            <span className="sidebar-logo-text">Seniorenbetreuung<br/>Antczak</span>
          </Link>
          <button className="sidebar-close" onClick={onClose} aria-label="Menü schließen">
            <Icon.Close size={18} />
          </button>
        </div>

        {/* ── Bereichsumschalter ── */}
        <div className="area-switcher">
          <button
            type="button"
            className="area-switcher__current"
            onClick={() => setShowAreaMenu(v => !v)}
            aria-expanded={showAreaMenu}
          >
            <span className="area-switcher__dot" style={{ backgroundColor: theme.accent }} />
            <span className="area-switcher__text">
              <span className="area-switcher__eyebrow">Bereich</span>
              <span className="area-switcher__label">{theme.label}</span>
            </span>
            <Icon.ChevronDown size={14} className={`area-switcher__chevron ${showAreaMenu ? 'open' : ''}`} />
          </button>

          {showAreaMenu && (
            <div className="area-switcher__menu" role="menu">
              {AREA_ORDER.map(a => {
                const at = AREA_THEME[a];
                const isActive = a === area;
                return (
                  <button
                    key={a}
                    type="button"
                    role="menuitemradio"
                    aria-checked={isActive}
                    className={`area-switcher__option ${isActive ? 'active' : ''}`}
                    onClick={() => handleSwitchArea(a)}
                  >
                    <span className="area-switcher__dot" style={{ backgroundColor: at.accent }} />
                    <span className="area-switcher__opt-label">{at.label}</span>
                    {isActive && <Icon.Check size={15} className="area-switcher__check" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item, i) => {
            // Überschrift, sobald ein neuer Arbeitsbereich beginnt.
            const neueGruppe = item.group && item.group !== menuItems[i - 1]?.group;
            return (
              <React.Fragment key={item.path}>
                {neueGruppe && <div className="sidebar-group-title">{item.group}</div>}
                <Link
                  to={item.path}
                  className={`sidebar-link ${location.pathname === item.path || location.pathname.startsWith((item.activePrefix ?? item.path) + '/') || location.pathname === item.activePrefix ? 'active' : ''}`}
                  onClick={onClose}
                >
                  <IconByName name={item.icon} size={18} className="sidebar-icon" />
                  <span className="sidebar-label">{item.label}</span>
                </Link>
              </React.Fragment>
            );
          })}
        </nav>

        {/* User Menu */}
        <div className="sidebar-footer">
          <div className="user-section">
            <div
              className="user-info"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div className="user-avatar">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div className="user-details">
                <div className="user-name">
                  {user?.firstName} {user?.lastName}
                </div>
                <div className="user-role">
                  {user?.role === 'ADMIN' ? 'Administrator' : 'Benutzer'}
                </div>
              </div>
              <div className="user-menu-toggle">
                {showUserMenu ? <Icon.ChevronDown size={14} /> : <Icon.ChevronUp size={14} />}
              </div>
            </div>

            {showUserMenu && (
              <div className="user-menu">
                <button
                  className="user-menu-item"
                  onClick={() => { setShowUserMenu(false); setShowPasswordForm(true); }}
                >
                  <Icon.Key size={16} className="user-menu-icon" />
                  <span>Passwort ändern</span>
                </button>
                <button
                  className="user-menu-item"
                  onClick={handleLogout}
                >
                  <Icon.Logout size={16} className="user-menu-icon" />
                  <span>Abmelden</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {showPasswordForm && <PasswordChangeForm onClose={() => setShowPasswordForm(false)} />}
    </>
  );
};

export default Sidebar;
